import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Smartphone,
  Globe,
  Palette,
  Users,
  Star,
  Clock,
  CheckCircle
} from 'lucide-react';

const Pricing = () => {
  const [activeTab, setActiveTab] = useState('packages');
  const [packages, setPackages] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Paketleri çek
      const packagesResponse = await axios.get('/pricing/packages');
      if (packagesResponse.data.success) {
        setPackages(packagesResponse.data.packages || []);
      }

      // Maaşları çek
      const salariesResponse = await axios.get('/pricing/salaries');
      if (salariesResponse.data.success) {
        setSalaries(salariesResponse.data.salaries || []);
      }
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      // API hatası durumunda fallback
      console.error('API bağlantısı başarısız:', error);
      setPackages([]);
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'mobile': return <Smartphone className="h-5 w-5" />;
      case 'web': return <Globe className="h-5 w-5" />;
      case 'design': return <Palette className="h-5 w-5" />;
      default: return <Star className="h-5 w-5" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'basic': return 'bg-green-100 text-green-800';
      case 'standard': return 'bg-blue-100 text-blue-800';
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'enterprise': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelText = (level) => {
    switch (level) {
      case 'basic': return 'Temel';
      case 'standard': return 'Standart';
      case 'premium': return 'Premium';
      case 'enterprise': return 'Kurumsal';
      default: return level;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Fiyatlandırma</h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('packages')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'packages'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Paketler
          </button>
          <button
            onClick={() => setActiveTab('salaries')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'salaries'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Maaşlar
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'packages' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map(pkg => (
            <div key={pkg.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                {getCategoryIcon(pkg.category)}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(pkg.level)}`}>
                  {getLevelText(pkg.level)}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{pkg.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
              <div className="mb-4">
                <div className="text-2xl font-bold text-gray-900">
                  ₺{pkg.minPrice?.toLocaleString()} - ₺{pkg.maxPrice?.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">{pkg.duration}</div>
              </div>
              {pkg.features && (
                <ul className="text-sm text-gray-600 space-y-1">
                  {pkg.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                  {pkg.features.length > 3 && (
                    <li className="text-gray-400">+{pkg.features.length - 3} özellik daha</li>
                  )}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'salaries' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {salaries.map(salary => (
            <div key={salary.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <Users className="h-6 w-6 text-blue-600" />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(salary.level)}`}>
                  {getLevelText(salary.level)}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{salary.position}</h3>
              <p className="text-gray-600 text-sm mb-4">{salary.description}</p>
              <div className="mb-4">
                <div className="text-2xl font-bold text-gray-900">
                  ₺{salary.minSalary?.toLocaleString()} - ₺{salary.maxSalary?.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Aylık maaş</div>
              </div>
              {salary.benefits && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Yan Haklar:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {salary.benefits.slice(0, 3).map((benefit, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {((activeTab === 'packages' && packages.length === 0) || 
        (activeTab === 'salaries' && salaries.length === 0)) && (
        <div className="text-center py-12">
          <Clock className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {activeTab === 'packages' ? 'Henüz paket eklenmemiş' : 'Henüz maaş bilgisi eklenmemiş'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeTab === 'packages' 
              ? 'Sistem henüz geliştirme aşamasında.' 
              : 'Maaş bilgileri yakında eklenecek.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Pricing;